import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import FormInput from '../forms/FormInput';
import SelectInput from '../forms/SelectInput';
import Button from '../common/Button';
import { useToast } from '../common/Toast';
import { categoryService } from '../../services/category.service';
import { useForm } from '../../hooks/useForm';
import { validators } from '../../utils/validators';
import { theme } from '../../config/theme';

const CategoryModal = ({ isOpen, onClose, category, allCategories }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const isEdit = !!category;

  const { values, errors, touched, handleChange, handleBlur, validate, setValues } = useForm(
    {
      name: '',
      slug: '',
      description: '',
      parent_id: '',
      is_active: true,
    },
    {
      name: (value) => validators.required(value, 'Category name'),
      slug: (value) => {
        if (!value) return null;
        if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) {
          return 'Slug must be lowercase letters, numbers, and hyphens only';
        }
        return null;
      },
    }
  );

  useEffect(() => {
    if (category) {
      setValues({
        name: category.name || '',
        slug: category.slug || '',
        description: category.description || '',
        parent_id: category.parent_id || '',
        is_active: category.is_active ?? true,
      });
      setImagePreview(category.image || null);
    } else {
      setImagePreview(null);
      setImageFile(null);
    }
  }, [category]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('name', values.name);
      if (values.description) submitData.append('description', values.description);
      if (values.slug) submitData.append('slug', values.slug);
      if (values.parent_id) submitData.append('parent_id', values.parent_id);
      submitData.append('is_active', values.is_active);
      if (imageFile) submitData.append('image', imageFile);

      if (isEdit) {
        await categoryService.update(category.id, submitData);
        toast.success('Category updated successfully');
      } else {
        await categoryService.create(submitData);
        toast.success('Category created successfully');
      }

      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const parentOptions = allCategories
    ?.filter(cat => !isEdit || cat.id !== category?.id)
    .filter(cat => cat.parent_id === null)
    .map(cat => ({ value: cat.id, label: cat.name })) || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose(false)}
      title={isEdit ? 'Edit Category' : 'Create Category'}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <FormInput
          label="Category Name"
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.name}
          touched={touched.name}
          placeholder="Electronics"
          required
        />

        <FormInput
          label="Slug (Optional)"
          name="slug"
          value={values.slug}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.slug}
          touched={touched.slug}
          placeholder="electronics (auto-generated if empty)"
        />

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{
            display: 'block',
            marginBottom: theme.spacing.xs,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.text.primary,
          }}>
            Category Image (Optional)
          </label>
          <div style={{
            border: `2px dashed ${theme.colors.gray[300]}`,
            borderRadius: theme.borderRadius.md,
            padding: theme.spacing.lg,
            textAlign: 'center',
            backgroundColor: theme.colors.gray[50],
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseOver={(e) => e.currentTarget.style.borderColor = theme.colors.primary.main}
          onMouseOut={(e) => e.currentTarget.style.borderColor = theme.colors.gray[300]}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
              }}
            />
            {imagePreview ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.sm }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: '120px',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.gray[200]}`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                />
                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.primary.main, fontWeight: theme.typography.fontWeight.medium }}>
                  Click or drag to change image
                </span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.xs, color: theme.colors.text.secondary }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.colors.gray[400] }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, marginTop: theme.spacing.sm }}>
                  Click to upload an image
                </span>
                <span style={{ fontSize: theme.typography.fontSize.xs }}>
                  SVG, PNG, JPG or GIF (max. 5MB)
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{
            display: 'block',
            marginBottom: theme.spacing.xs,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.text.primary,
          }}>
            Description (Optional)
          </label>
          <textarea
            name="description"
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Category description..."
            rows={3}
            style={{
              width: '100%',
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              fontSize: theme.typography.fontSize.sm,
              border: `1px solid ${theme.colors.gray[300]}`,
              borderRadius: theme.borderRadius.md,
              outline: 'none',
              fontFamily: theme.typography.fontFamily,
              resize: 'vertical',
            }}
          />
        </div>

        <SelectInput
          label="Parent Category (Optional)"
          name="parent_id"
          value={values.parent_id}
          onChange={handleChange}
          onBlur={handleBlur}
          options={parentOptions}
          placeholder="None (Root Category)"
        />

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="is_active"
              checked={values.is_active}
              onChange={(e) => handleChange({ target: { name: 'is_active', value: e.target.checked } })}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium }}>
              Active
            </span>
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: theme.spacing.xl }}>
          <Button 
            type="submit" 
            variant="primary"
            size="md"
            loading={loading} 
            disabled={loading}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CategoryModal;